import type { NassauMatchConfig } from '@/lib/games/engine/types';
import { defaultNassauMatches, roundRobinNassauMatches } from '@/lib/games/engine/nassau-matches';
import { getLegStakes } from '@/lib/games/engine/stakes-helpers';
import type { GameStakeConfig } from '@/lib/games/engine/types';

interface RosterEntry {
  player_key: string;
  name: string;
}

interface Props {
  formatId: 'nassau' | 'team-nassau';
  roster: RosterEntry[];
  teams?: string[][];
  matches: NassauMatchConfig[];
  defaultStakes: GameStakeConfig;
  onChange: (matches: NassauMatchConfig[]) => void;
}

function newId() {
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function legTemplate(stakes: GameStakeConfig) {
  return getLegStakes(stakes);
}

export function NassauMatchBuilder({
  formatId,
  roster,
  teams,
  matches,
  defaultStakes,
  onChange,
}: Props) {
  const isTeam = formatId === 'team-nassau';
  const keys = roster.map((p) => p.player_key);
  const legs = legTemplate(defaultStakes);

  const ensureDefaults = () => {
    if (matches.length) return matches;
    return defaultNassauMatches(formatId, keys, teams, legs);
  };

  const list = matches.length ? matches : ensureDefaults();

  const updateMatch = (id: string, patch: Partial<NassauMatchConfig>) => {
    onChange(list.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const setSide = (id: string, side: 'sideA' | 'sideB', playerKey: string) => {
    const m = list.find((x) => x.id === id);
    if (!m || isTeam) return;
    const other = side === 'sideA' ? 'sideB' : 'sideA';
    const nextSide = [playerKey];
    const otherSide = m[other].filter((k) => k !== playerKey);
    updateMatch(id, { [side]: nextSide, [other]: otherSide.length ? otherSide : m[other] });
  };

  const addMatch = () => {
    const used = new Set(list.flatMap((m) => [...m.sideA, ...m.sideB]));
    const avail = keys.filter((k) => !used.has(k));
    const a = avail[0] || keys[0];
    const b = avail[1] || keys.find((k) => k !== a) || keys[1];
    if (!a || !b) return;
    onChange([...list, {
      id: newId(),
      mode: 'singles',
      sideA: [a],
      sideB: [b],
      stakes: legs,
    }]);
  };

  const removeMatch = (id: string) => {
    if (list.length <= 1) return;
    onChange(list.filter((m) => m.id !== id));
  };

  const applyPreset = (preset: 'traditional' | 'round-robin' | 'team') => {
    if (preset === 'round-robin' && keys.length >= 4) {
      onChange(roundRobinNassauMatches(keys, legs));
      return;
    }
    if (preset === 'team' && teams && teams.length >= 2) {
      onChange(defaultNassauMatches('team-nassau', keys, teams, legs));
      return;
    }
    onChange(defaultNassauMatches('nassau', keys, teams, legs));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        <PresetBtn label="1v1" onClick={() => applyPreset('traditional')} />
        {keys.length >= 4 && !isTeam && (
          <PresetBtn label="Round robin (6)" onClick={() => applyPreset('round-robin')} />
        )}
        {isTeam && teams && teams.length >= 2 && (
          <PresetBtn label="Team 2v2" onClick={() => applyPreset('team')} />
        )}
      </div>

      {list.map((m, idx) => {
        const name = (k: string) => roster.find((p) => p.player_key === k)?.name.split(' ')[0] || 'Player';
        const mLegs = m.stakes ?? legs;
        return (
          <div key={m.id} className="rounded-lg border border-line bg-cream/50 p-2.5">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[11px] font-bold text-navy">
                Match {idx + 1}
                {isTeam
                  ? ` · ${m.sideA.map(name).join(' & ')} vs ${m.sideB.map(name).join(' & ')}`
                  : ` · ${name(m.sideA[0])} vs ${name(m.sideB[0])}`}
              </div>
              {list.length > 1 && (
                <button type="button" onClick={() => removeMatch(m.id)} className="text-[10px] font-semibold text-bad">
                  Remove
                </button>
              )}
            </div>

            {!isTeam && (
              <div className="mb-2 grid grid-cols-2 gap-2">
                <SidePicker
                  label="Side A"
                  roster={roster}
                  selected={m.sideA[0]}
                  exclude={m.sideB[0]}
                  onPick={(k) => setSide(m.id, 'sideA', k)}
                />
                <SidePicker
                  label="Side B"
                  roster={roster}
                  selected={m.sideB[0]}
                  exclude={m.sideA[0]}
                  onPick={(k) => setSide(m.id, 'sideB', k)}
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-1.5">
              {(['front', 'back', 'total'] as const).map((leg) => (
                <label key={leg} className="text-center">
                  <div className="text-[9px] font-bold uppercase text-mute">
                    {leg === 'front' ? 'Front' : leg === 'back' ? 'Back' : 'Overall'}
                  </div>
                  <input
                    type="number"
                    min={0}
                    value={mLegs[leg]}
                    onChange={(e) => {
                      const v = Math.max(0, Number(e.target.value) || 0);
                      updateMatch(m.id, { stakes: { ...mLegs, [leg]: v } });
                    }}
                    className="mt-0.5 w-full rounded border border-line bg-paper px-1 py-1 text-center text-xs"
                  />
                </label>
              ))}
            </div>
          </div>
        );
      })}

      {!isTeam && keys.length > 2 && (
        <button
          type="button"
          onClick={addMatch}
          className="w-full rounded-lg border border-dashed border-navy/30 py-2 text-xs font-bold text-navy"
        >
          + Add matchup
        </button>
      )}

      <p className="text-[10px] leading-relaxed text-mute">
        Each matchup is an independent Nassau (front, back, overall). Net scoring uses strokes relative to the lowest handicap in the group.
      </p>
    </div>
  );
}

function PresetBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-line bg-paper px-2.5 py-1 text-[10px] font-bold text-navy"
    >
      {label}
    </button>
  );
}

function SidePicker({
  label,
  roster,
  selected,
  exclude,
  onPick,
}: {
  label: string;
  roster: RosterEntry[];
  selected?: string;
  exclude?: string;
  onPick: (key: string) => void;
}) {
  return (
    <div>
      <div className="mb-1 text-[9px] font-bold uppercase text-mute">{label}</div>
      <div className="flex flex-wrap gap-1">
        {roster.filter((p) => p.player_key !== exclude).map((p) => (
          <button
            key={p.player_key}
            type="button"
            onClick={() => onPick(p.player_key)}
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              selected === p.player_key ? 'bg-navy text-cream' : 'border border-line bg-paper text-ink-soft'
            }`}
          >
            {p.name.split(' ')[0]}
          </button>
        ))}
      </div>
    </div>
  );
}
